/**
 * CP-12 configurable candidate intake fields — the pure half of the Settings
 * card (`CandidateIntakeFieldsCard`) and of the candidate Gate.
 *
 * A company may ask every candidate for a few extra details before the test
 * starts (name + email are always asked). The config is a list of
 * `{ key, label, type, required }` rows, at most `MAX_INTAKE_FIELDS`; a PRESET
 * is a row with a fixed key + type (so the Gate renders the input its name
 * promises) and an editable label, a CUSTOM row carries a generated
 * `custom_xxxxxx` key and any type.
 *
 * Two rules live here so no caller can get them wrong:
 *
 *  1. `intakeFieldsDirty` compares the NORMALIZED lists — exactly what
 *     `normalizeIntakeFields` sends on save — so a whitespace-only label edit
 *     is not a change and Save settles the moment the server echoes the row
 *     (memory project_hydrated_form_dirty_must_equal_draft).
 *  2. `intakeValueError` mirrors the server's per-type rules
 *     (backend/candidate_intake.py) so the Gate can gate Submit and show the
 *     same message inline. The server stays the trust boundary; a mismatch
 *     here only costs the candidate a round-trip.
 *
 * `INTAKE_PRESETS` MUST stay identical to `PRESETS` in
 * backend/candidate_intake.py — `backend/tests/test_candidate_intake.py`
 * fails if the two drift (a preset offered here that the API refuses would be
 * a 422 on a row the form itself suggested).
 */

export type IntakeFieldType = "text" | "phone" | "integer" | "url";

export interface IntakeField {
  key: string;
  label: string;
  type: IntakeFieldType;
  required: boolean;
}

// Mirrors of the backend bounds (backend/candidate_intake.py) — server is the
// source of truth; these gate Save / Submit and drive the inline hints.
export const MAX_INTAKE_FIELDS = 8;
export const MAX_LABEL_LEN = 80;
export const MAX_TEXT_LEN = 200;
export const MAX_URL_LEN = 300;
export const INTEGER_MIN = 0;
export const INTEGER_MAX = 9999;
const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
const PHONE_SEPARATORS = /[\s\-.()]/g;
const PHONE_RE = /^\+?\d{6,15}$/;

/** The one phone rule in the product: digits (an optional leading `+`) once
 *  the separators people type are stripped. A candidate's intake answer and a
 *  recruiter's own number (FEAT-23: sign-up, Profile) both go through it.
 *  Mirror of `looks_like_phone` in backend/candidate_intake.py — which spells
 *  out JS's own `\d` (0-9 only) and `\s`; the parity table is pinned in both
 *  candidateIntake.test.ts and test_recruiter_contact_fields.py. */
export function looksLikePhone(value: string): boolean {
  return PHONE_RE.test(value.replace(PHONE_SEPARATORS, ""));
}

/** A `tel:` href for a number stored as typed. An international number drops
 *  the "(0)" people write for the national trunk prefix ("+44 (0)20 …" is
 *  dialled +4420…); a national one keeps its 0. */
export function phoneTelHref(value: string): string {
  const v = value.trim();
  const dial = v.startsWith("+") ? v.replace(/\(\s*0\s*\)/g, "") : v;
  return `tel:${dial.replace(/[^\d+]/g, "")}`;
}
const URL_SCHEME_RE = /^https?:\/\//i;

export const INTAKE_TYPES: readonly IntakeFieldType[] = [
  "text",
  "phone",
  "integer",
  "url",
];

/** One-click presets. Key + type are fixed per preset; the label is a default
 *  the recruiter may rewrite (Polish, say). One entry per line on purpose —
 *  the backend parity test parses this array. */
export const INTAKE_PRESETS: ReadonlyArray<{
  key: string;
  label: string;
  type: IntakeFieldType;
}> = [
  { key: "phone", label: "Phone number", type: "phone" },
  { key: "years_experience", label: "Years of experience", type: "integer" },
  { key: "current_role", label: "Current job title", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "linkedin_url", label: "LinkedIn profile", type: "url" },
];

export const INTAKE_TYPE_OPTIONS: ReadonlyArray<{
  value: IntakeFieldType;
  label: string;
}> = [
  { value: "text", label: "Short text" },
  { value: "phone", label: "Phone" },
  { value: "integer", label: "Whole number" },
  { value: "url", label: "Link" },
];

export function isPresetKey(key: string): boolean {
  return INTAKE_PRESETS.some((p) => p.key === key);
}

/** A fresh, optional row for a preset (throws on an unknown key — the menu
 *  only ever offers known ones). */
export function presetField(key: string): IntakeField {
  const preset = INTAKE_PRESETS.find((p) => p.key === key);
  if (!preset) throw new Error(`Unknown intake preset: ${key}`);
  return { key: preset.key, label: preset.label, type: preset.type, required: false };
}

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i += 1) {
      buf[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** A fresh custom row: generated `custom_` + 6 hex key, blank label (the row
 *  is invalid until the recruiter names it), short text, optional. */
export function newCustomField(): IntakeField {
  return { key: `custom_${randomHex(3)}`, label: "", type: "text", required: false };
}

/** The list exactly as it is sent on save: labels trimmed, `required` coerced
 *  to a boolean, nothing dropped (an unnamed row is INVALID, not ignored —
 *  silently removing it would save something other than what is on screen). */
export function normalizeIntakeFields(rows: readonly IntakeField[]): IntakeField[] {
  return rows.map((r) => ({
    key: r.key,
    label: r.label.trim(),
    type: r.type,
    required: Boolean(r.required),
  }));
}

/** Dirty on the NORMALIZED lists — order matters (it is the Gate order),
 *  surrounding whitespace does not. */
export function intakeFieldsDirty(
  rows: readonly IntakeField[],
  saved: readonly IntakeField[],
): boolean {
  return (
    JSON.stringify(normalizeIntakeFields(rows)) !==
    JSON.stringify(normalizeIntakeFields(saved))
  );
}

/** Per-row problem, or `null`. Mirrors `IntakeField`'s validators. */
export function intakeFieldError(row: IntakeField): string | null {
  if (!KEY_RE.test(row.key)) return "Invalid field key.";
  const label = row.label.trim();
  if (!label) return "Give this field a label.";
  if (label.length > MAX_LABEL_LEN)
    return `Keep the label to ${MAX_LABEL_LEN} characters.`;
  if (!INTAKE_TYPES.includes(row.type)) return "Choose a field type.";
  const preset = INTAKE_PRESETS.find((p) => p.key === row.key);
  if (preset && preset.type !== row.type)
    return `${preset.label} is always a ${preset.type} field.`;
  return null;
}

/** The whole list can be saved: at most 8 rows, unique keys, every row valid. */
export function intakeFieldsValid(rows: readonly IntakeField[]): boolean {
  if (rows.length > MAX_INTAKE_FIELDS) return false;
  const keys = new Set(rows.map((r) => r.key));
  if (keys.size !== rows.length) return false;
  return rows.every((r) => intakeFieldError(r) === null);
}

/**
 * The candidate-side check for one field's raw input. `null` = fine (including
 * a blank OPTIONAL field). Same rules and near-identical wording to the server,
 * which re-checks everything at /start.
 */
export function intakeValueError(
  field: Pick<IntakeField, "label" | "type" | "required">,
  raw: string,
): string | null {
  const value = raw.trim();
  if (!value) return field.required ? `Please provide ${field.label}.` : null;
  switch (field.type) {
    case "text":
      return value.length > MAX_TEXT_LEN
        ? `${field.label} must be at most ${MAX_TEXT_LEN} characters.`
        : null;
    case "phone":
      return looksLikePhone(value)
        ? null
        : `${field.label} doesn't look like a valid phone number.`;
    case "integer": {
      if (!/^\d+$/.test(value)) return `${field.label} must be a whole number.`;
      const n = Number(value);
      return n >= INTEGER_MIN && n <= INTEGER_MAX
        ? null
        : `${field.label} must be between ${INTEGER_MIN} and ${INTEGER_MAX}.`;
    }
    case "url": {
      const withScheme = URL_SCHEME_RE.test(value) ? value : `https://${value}`;
      const rest = withScheme.slice(withScheme.indexOf("://") + 3);
      return withScheme.length > MAX_URL_LEN || /\s/.test(withScheme) || !rest.includes(".")
        ? `${field.label} doesn't look like a valid link.`
        : null;
    }
    default:
      return null;
  }
}

/** Native input attributes for a field type, so a phone gets the tel keypad
 *  and a number the numeric one. Always `type="text"` for url: a native
 *  `type="url"` refuses "linkedin.com/in/…" without a scheme, which the server
 *  accepts (it prepends https://). */
export function intakeInputAttrs(type: IntakeFieldType): {
  type: string;
  inputMode?: "tel" | "numeric" | "url" | "text";
  autoComplete?: string;
  placeholder: string;
} {
  switch (type) {
    case "phone":
      return { type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "+48 601 234 567" };
    case "integer":
      return { type: "text", inputMode: "numeric", placeholder: "5" };
    case "url":
      return { type: "text", inputMode: "url", autoComplete: "url", placeholder: "linkedin.com/in/your-name" };
    case "text":
    default:
      return { type: "text", placeholder: "" };
  }
}

/** Display form of a frozen answer: "Not provided" for a skipped field. */
export function formatIntakeValue(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "Not provided";
  return String(value);
}
